/**
 * MedsChipInput — Feb 28, 2026
 *
 * Adds a chip-row of prescribed medications next to the existing boolean
 * "Meds Rx'd" toggle. Each chip is `Name • dose freq` e.g.
 * `Ibuprofen • 600 mg q6h`. The popover lets the provider pick from a
 * top-20 favorites list (built into the constants) or type a custom
 * name, then tap dose + frequency pills (qd / bid / tid / qid / q6h /
 * qhs / prn). Optional duration "× 7 d" tail.
 *
 * Data lives in `medsRxdDetail` as a comma-separated string so the
 * existing LLM-prompt path can pass it verbatim as additional context
 * about what the provider actually prescribed (turns the boolean "true"
 * into a structured list when it matters, while still leaving the
 * boolean intact for prompt logic that just needs to know "yes meds
 * were given"). On its own, the boolean toggle continues to function
 * exactly as before — adding chips is purely additive.
 */
import React, { useState, useEffect } from 'react';
import { Pill } from 'lucide-react';
import axios from 'axios';
import { Input } from '../ui/input';
import { OrderChipRow } from './OrderChipRow';

// 20 most-common in primary care + soldier-readiness clinics.
const COMMON_MEDS = [
  'Ibuprofen', 'Acetaminophen', 'Naproxen', 'Diclofenac',
  'Cyclobenzaprine', 'Tizanidine', 'Methocarbamol',
  'Pantoprazole', 'Omeprazole', 'Famotidine',
  'Ondansetron', 'Promethazine',
  'Loratadine', 'Fluticasone nasal', 'Azelastine',
  'Albuterol HFA', 'Fluticasone-salmeterol',
  'Amoxicillin', 'Doxycycline', 'Azithromycin',
];

const FREQ_PILLS = ['qd', 'bid', 'tid', 'qid', 'q4h', 'q6h', 'q8h', 'q12h', 'qhs', 'qam', 'prn', 'AC', 'PC'];

/** Parse the `medsRxdDetail` string into an array of structured entries.
 *  Format on disk: "Ibuprofen • 600 mg q6h × 7 d, Pantoprazole • 40 mg qd"  */
export const parseMedsDetail = (str) => {
  if (!str || typeof str !== 'string') return [];
  return str
    .split(/,\s*/)
    .map((entry) => {
      const t = entry.trim();
      if (!t) return null;
      // Best-effort: split on " • " — falls back to whole entry as name.
      const bullet = t.indexOf(' • ');
      if (bullet === -1) return { name: t, dose: '', freq: '', duration: '' };
      const name = t.slice(0, bullet).trim();
      const rest = t.slice(bullet + 3).trim();
      // rest example: "600 mg q6h × 7 d"
      const durMatch = rest.match(/\s×\s(.+)$/);
      const duration = durMatch ? durMatch[1].trim() : '';
      const head = durMatch ? rest.slice(0, durMatch.index).trim() : rest;
      // Split head on last whitespace into "<dose> <freq>"
      const lastSp = head.lastIndexOf(' ');
      if (lastSp === -1) return { name, dose: head, freq: '', duration };
      return {
        name,
        dose: head.slice(0, lastSp).trim(),
        freq: head.slice(lastSp + 1).trim(),
        duration,
      };
    })
    .filter(Boolean);
};

export const formatMedEntry = (m) => {
  const parts = [];
  if (m.dose) parts.push(m.dose);
  if (m.freq) parts.push(m.freq);
  let tail = parts.join(' ');
  if (m.duration) tail = `${tail} × ${m.duration}`;
  return tail ? `${m.name} • ${tail}` : m.name;
};

export const joinMedsDetail = (arr) =>
  (Array.isArray(arr) ? arr : []).map(formatMedEntry).join(', ');

const MedEditor = ({ value, onSave, onCancel, recentMeds = [] }) => {
  const [name, setName] = useState(value?.name || '');
  const [dose, setDose] = useState(value?.dose || '');
  const [freq, setFreq] = useState(value?.freq || '');
  const [duration, setDuration] = useState(value?.duration || '');

  const pickName = (n) => setName(n);

  /** Apply a "Last 5 prescribed" chip — auto-fills ALL four fields so the
   *  provider re-prescribes the exact prior Rx with a single tap. */
  const applyRecent = (m) => {
    setName(m.name || '');
    setDose(m.dose || '');
    setFreq(m.freq || '');
    setDuration(m.duration || '');
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave({ name: trimmed, dose: dose.trim(), freq: freq.trim(), duration: duration.trim() });
  };

  return (
    <div className="space-y-2" data-testid="meds-editor">
      {/* Feb 28, 2026 — "Last 5 prescribed" quick-pick row. Server-side
          dedup by drug name (newest dose wins) so re-prescribing the
          exact prior Rx is a single tap. Hidden when the user has no
          prior chip-tagged prescriptions (cold start / guest mode). */}
      {recentMeds && recentMeds.length > 0 && (
        <div data-testid="meds-recent-row">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
            <span>Last {recentMeds.length} prescribed</span>
            <span className="text-muted-foreground/50 font-normal normal-case">— tap to refill</span>
          </div>
          <div className="flex flex-wrap gap-1 mb-1">
            {recentMeds.map((m, i) => {
              const tail = [m.dose, m.freq].filter(Boolean).join(' ');
              const full = [m.name, tail].filter(Boolean).join(' • ');
              return (
                <button
                  key={`${m.name}-${i}`}
                  type="button"
                  onClick={() => applyRecent(m)}
                  className="text-[10.5px] font-semibold transition-colors hover:opacity-90"
                  style={{
                    borderRadius: 3,
                    border: '1px solid rgba(255,123,134,0.55)',
                    background: 'rgba(255,123,134,0.16)',
                    color: '#ff7b86',
                    padding: '3px 7px',
                    lineHeight: 1.3,
                    cursor: 'pointer',
                    maxWidth: '280px',
                  }}
                  title={`Refill: ${full}${m.duration ? ` × ${m.duration}` : ''}`}
                  data-testid={`meds-recent-${i}`}
                >
                  <span className="truncate inline-block max-w-full">{full}{m.duration ? ` × ${m.duration}` : ''}</span>
                </button>
              );
            })}
          </div>
          <div className="border-t border-border/40 mt-1.5 mb-0.5"></div>
        </div>
      )}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Medication</div>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Type or pick below…"
          className="h-8 text-xs mb-1.5"
          data-testid="meds-name"
          autoFocus={!value}
        />
        <div className="flex flex-wrap gap-1">
          {COMMON_MEDS.slice(0, 12).map((m) => {
            const on = name === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => pickName(m)}
                className="px-2 py-0.5 text-[10.5px] font-semibold"
                style={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: on ? 'rgba(255,123,134,0.55)' : 'rgba(255,255,255,0.14)',
                  background: on ? 'rgba(255,123,134,0.14)' : 'transparent',
                  color: on ? '#ff7b86' : 'var(--muted-foreground)',
                  cursor: 'pointer',
                }}
                data-testid={`meds-pill-${m.toLowerCase().replace(/[\s/]/g, '-')}`}
              >{m}</button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Dose</div>
            <Input
              value={dose}
              onChange={(e) => setDose(e.target.value)}
              placeholder="600 mg"
              className="h-8 text-xs"
              data-testid="meds-dose"
            />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Duration</div>
            <Input
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="7 d"
              className="h-8 text-xs"
              data-testid="meds-duration"
            />
          </div>
        </div>
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Frequency</div>
        <div className="flex flex-wrap gap-1">
          {FREQ_PILLS.map((f) => {
            const on = freq === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFreq(f)}
                className="px-2 py-0.5 text-[10.5px] font-semibold"
                style={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: on ? 'rgba(255,123,134,0.55)' : 'rgba(255,255,255,0.14)',
                  background: on ? 'rgba(255,123,134,0.14)' : 'transparent',
                  color: on ? '#ff7b86' : 'var(--muted-foreground)',
                  cursor: 'pointer',
                }}
                data-testid={`meds-freq-${f}`}
              >{f}</button>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end gap-1.5 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground border border-border hover:border-border-hot"
          style={{ borderRadius: 3, background: 'transparent' }}
          data-testid="meds-cancel"
        >Cancel</button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!name.trim()}
          className="px-3 py-1.5 text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderRadius: 3, background: '#ff7b86', color: '#0f1419', border: '1px solid #ff7b86' }}
          data-testid="meds-save"
        >Save</button>
      </div>
    </div>
  );
};

const renderMedChip = (m) => {
  const detailParts = [];
  if (m.dose) detailParts.push(m.dose);
  if (m.freq) detailParts.push(m.freq);
  let detail = detailParts.join(' ');
  if (m.duration) detail = detail ? `${detail} × ${m.duration}` : `× ${m.duration}`;
  return { label: m.name, detail };
};

export const MedsChipInput = ({ medsRxdDetail, setMedsRxdDetail, onAddTriggersBoolean }) => {
  const items = parseMedsDetail(medsRxdDetail);

  // Feb 28, 2026 — fetch the user's recent meds once on mount so the
  // editor popover can surface "Last 5 prescribed" quick-pick chips.
  // Recent-meds is a CROSS-ENCOUNTER history view — it intentionally
  // doesn't refresh with each new chip in the current encounter (those
  // already appear in the chip row itself). One fetch per mount keeps
  // network noise down on a high-tempo clinic day.
  const [recentMeds, setRecentMeds] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const API = window.__WR_API_BASE__;
    if (!API) return undefined;
    axios.get(`${API}/api/me/recent-meds`, { withCredentials: true })
      .then((r) => { if (!cancelled) setRecentMeds(r?.data?.meds || []); })
      .catch(() => { /* 401 = guest mode — empty array is fine */ });
    return () => { cancelled = true; };
  }, []); // intentionally once on mount — see comment above

  const persist = (next) => setMedsRxdDetail(joinMedsDetail(next));

  const handleAdd = (entry) => {
    persist([...items, entry]);
    // Adding a med implicitly flips "Meds Rx'd" boolean ON so the LLM
    // knows meds were prescribed without the provider having to toggle
    // the parent checkbox separately.
    if (typeof onAddTriggersBoolean === 'function') onAddTriggersBoolean();
  };

  const handleUpdate = (index, entry) => {
    const next = items.map((m, i) => (i === index ? entry : m));
    persist(next);
  };

  const handleRemove = (index) => persist(items.filter((_, i) => i !== index));

  return (
    <OrderChipRow
      items={items}
      renderChip={renderMedChip}
      renderEditor={({ value, onSave, onCancel }) => (
        <MedEditor value={value} onSave={onSave} onCancel={onCancel} recentMeds={recentMeds} />
      )}
      addLabel="Add med"
      accent="rose"
      sectionTitle="Meds Rx'd"
      sectionIcon={<Pill className="w-3.5 h-3.5 text-rose-400" />}
      testIdPrefix="meds-chip"
      onAdd={handleAdd}
      onUpdate={handleUpdate}
      onRemove={handleRemove}
    />
  );
};

export default MedsChipInput;
